import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import client, { resolveMedia } from "../lib/api";
import VideoPlayer from "../components/VideoPlayer";
import VideoCard from "../components/VideoCard";
import ShareModal from "../components/ShareModal";
import AgeGateModal from "../components/AgeGateModal";
import { Eye, Calendar, Tag, ChevronLeft, Share2, ShieldAlert } from "lucide-react";

export default function Watch() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const loadVideo = useCallback(async () => {
    try {
      const [vRes, rRes] = await Promise.all([
        client.get(`/videos/${id}`),
        client.get(`/videos/${id}/related`),
      ]);
      setVideo(vRes.data);
      setRelated(rRes.data);
      if (vRes.data?.age_gate_required) {
        setGateOpen(true);
      } else {
        client.post(`/videos/${id}/view`).catch(() => {});
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Video unavailable.");
    }
  }, [id]);

  useEffect(() => { loadVideo(); }, [loadVideo]);

  // Per-video head management — runs when video data loads. Mirrors the exact
  // values the backend `/api/ssr/watch/{id}` endpoint computes so JS-executing
  // crawlers (Googlebot, Bingbot) and users with direct /watch/{id} nav see
  // the correct per-video metadata without the Emergent platform-owned edge
  // worker having to be reconfigured. Non-JS social crawlers (FB/TikTok/X)
  // continue to be served the SSR surface via the share-modal links.
  // Same pattern as LandingPage.jsx — upsert in <head>, no cleanup, each
  // route's effect overwrites the previous one.
  useEffect(() => {
    if (!video) return;
    const origin = window.location.origin;
    const abs = (u) => {
      if (!u) return "";
      if (u.startsWith("http")) return u;
      return `${origin}${u.startsWith("/") ? "" : "/"}${u}`;
    };
    // Preferred canonical: the public /v/{slug} landing page (indexable
    // SEO-optimized). Fall back to /watch/{id} for legacy records with
    // no slug stored.
    const slug = (video.slug || "").trim();
    const canonical = slug ? `${origin}/v/${slug}` : `${origin}/watch/${video.id}`;
    const rawTitle = (video.title || "Untitled").trim();
    const title = `${rawTitle} | NexusPlayer`;
    const descRaw = (video.description || "").replace(/\n/g, " ").trim();
    const desc = descRaw.slice(0, 300)
      || `Watch "${rawTitle}" on NexusPlayer — TheSaiyanVictoria's private streaming home for POV, shorts, and series.`;
    const thumb = video.thumbnail ? abs(video.thumbnail) : `${origin}/api/og-fallback.svg`;
    const playback = video.playback_url ? abs(video.playback_url) : "";
    const isShort = video.type === "short";

    document.title = title;

    const ensure = (selector, attrKey, attrVal, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrKey, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    ensure('meta[name="description"]', "name", "description", desc);
    ensure('meta[name="application-name"]', "name", "application-name", "NexusPlayer");
    ensure('meta[name="robots"]', "name", "robots",
      (video.age_rating === "R" || video.age_rating === "Explicit")
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    ensure('meta[property="og:site_name"]', "property", "og:site_name", "NexusPlayer");
    ensure('meta[property="og:type"]', "property", "og:type", "video.other");
    ensure('meta[property="og:title"]', "property", "og:title", rawTitle);
    ensure('meta[property="og:description"]', "property", "og:description", desc);
    ensure('meta[property="og:url"]', "property", "og:url", canonical);
    ensure('meta[property="og:image"]', "property", "og:image", thumb);
    ensure('meta[property="og:image:width"]', "property", "og:image:width", isShort ? "1080" : "1920");
    ensure('meta[property="og:image:height"]', "property", "og:image:height", isShort ? "1920" : "1080");
    if (playback) {
      ensure('meta[property="og:video"]', "property", "og:video", playback);
      ensure('meta[property="og:video:secure_url"]', "property", "og:video:secure_url", playback);
      ensure('meta[property="og:video:type"]', "property", "og:video:type", "video/mp4");
      ensure('meta[property="og:video:width"]', "property", "og:video:width", isShort ? "1080" : "1920");
      ensure('meta[property="og:video:height"]', "property", "og:video:height", isShort ? "1920" : "1080");
    }
    ensure('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    ensure('meta[name="twitter:title"]', "name", "twitter:title", rawTitle);
    ensure('meta[name="twitter:description"]', "name", "twitter:description", desc);
    ensure('meta[name="twitter:image"]', "name", "twitter:image", thumb);

    // Single canonical — overwrite in place to prevent duplicates.
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // VideoObject + BreadcrumbList JSON-LD — mirrors backend _ssr_shell_html.
    const videoObject = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: rawTitle,
      description: desc,
      thumbnailUrl: [thumb],
      uploadDate: video.created_at || new Date().toISOString(),
      url: canonical,
    };
    if (playback) {
      if (video.source_type === "upload") videoObject.contentUrl = playback;
      else videoObject.embedUrl = playback;
    }
    if (typeof video.duration === "number" && video.duration > 0) {
      const s = Math.round(video.duration);
      videoObject.duration = `PT${Math.floor(s / 3600)}H${Math.floor((s % 3600) / 60)}M${s % 60}S`;
    }
    if (video.age_rating && video.age_rating !== "G") videoObject.contentRating = video.age_rating;
    if (video.category) videoObject.genre = video.category;
    if (Array.isArray(video.tags) && video.tags.length) videoObject.keywords = video.tags.join(", ");
    if (typeof video.views === "number" && video.views > 0) {
      videoObject.interactionStatistic = {
        "@type": "InteractionCounter",
        interactionType: { "@type": "https://schema.org/WatchAction" },
        userInteractionCount: video.views,
      };
    }
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "NexusPlayer", item: `${origin}/` },
      ],
    };
    if (video.category) breadcrumb.itemListElement.push({
      "@type": "ListItem", position: 2, name: video.category,
      item: `${origin}/library?category=${encodeURIComponent(video.category)}`,
    });
    breadcrumb.itemListElement.push({
      "@type": "ListItem",
      position: video.category ? 3 : 2,
      name: rawTitle, item: canonical,
    });

    const upsertJsonLd = (dataKey, obj) => {
      const sel = `script[type="application/ld+json"][data-nexus="${dataKey}"]`;
      let script = document.head.querySelector(sel);
      if (!script) {
        script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-nexus", dataKey);
        document.head.appendChild(script);
      }
      // JSON.stringify is safe here — React doesn't need escape; browser parser
      // reads <script> text literally.
      script.textContent = JSON.stringify(obj);
    };
    upsertJsonLd("videoobject", videoObject);
    upsertJsonLd("breadcrumb", breadcrumb);
  }, [video]);

  if (error) {
    return (
      <div className="px-6 py-20 text-center" data-testid="watch-error">
        <h2 className="text-2xl font-bold text-white">Signal lost</h2>
        <p className="mt-2 text-zinc-400">{error}</p>
        <Link to="/" className="mt-6 inline-block text-[#FFB800] uppercase text-xs tracking-widest">Return Home</Link>
      </div>
    );
  }
  if (!video) return <div className="px-6 py-20 text-center text-zinc-500" data-testid="watch-loading">Tuning...</div>;

  const playbackUrl = resolveMedia(video.playback_url);
  const poster = resolveMedia(video.thumbnail);
  const isGated = video.age_gate_required;

  return (
    <div data-testid="watch-page" className="px-4 sm:px-8 lg:px-12 py-6">
      <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1 text-zinc-400 hover:text-white text-xs uppercase tracking-widest mb-4">
        <ChevronLeft className="w-4 h-4" /> Back
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          {isGated ? (
            <div
              data-testid="watch-gated-placeholder"
              className="w-full aspect-video rounded-xl bg-gradient-to-br from-black to-zinc-900 border border-[#FF6B00]/30 flex flex-col items-center justify-center gap-4 p-6 text-center"
              style={{ backgroundImage: poster ? `linear-gradient(rgba(0,0,0,0.85), rgba(0,0,0,0.95)), url(${poster})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}
            >
              <ShieldAlert className="w-12 h-12 text-[#FF6B00]" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Adult Content</p>
                <h3 className="text-2xl font-black text-white mt-1">Age verification required</h3>
                <p className="text-sm text-zinc-400 mt-2 max-w-md">Confirm your birth year to unlock this drop.</p>
              </div>
              <button
                onClick={() => setGateOpen(true)}
                data-testid="watch-open-gate"
                className="h-11 px-6 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FFB800] text-black font-black uppercase tracking-widest text-xs"
              >
                Verify Age
              </button>
            </div>
          ) : (
            <VideoPlayer src={playbackUrl} poster={poster} vertical={video.type === "short"} videoId={video.id} />
          )}
          <div className="mt-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">{video.category}</span>
              {video.age_rating && video.age_rating !== "G" && (
                <span
                  data-testid="age-rating-badge"
                  className={`text-[10px] font-black px-2 py-0.5 rounded ${
                    video.age_rating === "Explicit" ? "bg-red-500/20 text-red-300 border border-red-500/40" :
                    video.age_rating === "R" ? "bg-orange-500/20 text-orange-300 border border-orange-500/40" :
                    video.age_rating === "PG-13" ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40" :
                    "bg-zinc-500/20 text-zinc-300 border border-zinc-500/40"
                  }`}
                >
                  {video.age_rating}
                </span>
              )}
            </div>
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-1 leading-tight">{video.title}</h1>
              <button
                onClick={() => setShareOpen(true)}
                data-testid="watch-share-btn"
                className="flex-shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-zinc-900 border border-white/10 hover:border-[#FF6B00]/50 hover:text-[#FFB800] text-xs uppercase tracking-widest text-white"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-zinc-500 uppercase tracking-wider">
              <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {video.views || 0} views</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {new Date(video.created_at).toLocaleDateString()}</span>
              {video.tags && video.tags.length > 0 && (
                <span className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {video.tags.join(", ")}</span>
              )}
              {video.series && (
                <Link to={`/library?series=${encodeURIComponent(video.series)}`} className="text-[#FFB800] hover:underline">Part of: {video.series}</Link>
              )}
            </div>
            {video.description && (
              <div className="mt-5 p-5 rounded-xl bg-zinc-900/60 border border-white/5">
                <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{video.description}</p>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-4" data-testid="related-sidebar">
          <h3 className="text-xs uppercase tracking-[0.3em] text-[#FFB800] font-bold">Related from the Nexus</h3>
          <div className="grid grid-cols-1 gap-3">
            {related.length === 0 && <p className="text-sm text-zinc-500">No related videos yet.</p>}
            {related.map((r) => <VideoCard key={r.id} video={r} size="lg" />)}
          </div>
        </aside>
      </div>
      {shareOpen && <ShareModal video={video} onClose={() => setShareOpen(false)} />}
      <AgeGateModal
        open={gateOpen}
        videoTitle={video?.title}
        rating={video?.age_rating}
        minAge={video?.age_gate_min_age || 18}
        onVerified={() => { setGateOpen(false); loadVideo(); }}
        onCancel={isGated ? null : () => setGateOpen(false)}
      />
    </div>
  );
}
